import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// Value in/out is ISO 'YYYY-MM-DD' (same as <input type="date">). Shows "30 Sep 2026".
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const pad = n => String(n).padStart(2, '0')
const iso = (y, m, d) => y + '-' + pad(m + 1) + '-' + pad(d)
const todayIso = () => { const t = new Date(); return iso(t.getFullYear(), t.getMonth(), t.getDate()) }
const valid = (y, m, d) => { const x = new Date(y, m, d); return x.getFullYear() === y && x.getMonth() === m && x.getDate() === d }

export function fmtDocDate(s) {
  const v = parseDate(s)
  if (!v) return s || ''
  const [y, m, d] = v.split('-').map(Number)
  return d + ' ' + MONTHS[m - 1] + ' ' + y
}
export function parseDate(s) {
  if (!s) return ''
  const t = String(s).trim()
  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (m) { const y = +m[1], mo = +m[2] - 1, d = +m[3]; return valid(y, mo, d) ? iso(y, mo, d) : '' }
  m = t.match(/^(\d{1,2})[/.\-\s](\d{1,2})[/.\-\s](\d{2,4})$/) // DD/MM/YYYY (European)
  if (m) { let y = +m[3]; if (y < 100) y += 2000; const mo = +m[2] - 1, d = +m[1]; return valid(y, mo, d) ? iso(y, mo, d) : '' }
  m = t.match(/^(\d{1,2})\s+([A-Za-z]{3,})\.?\s+(\d{4})$/) // 30 Sep 2026
  if (m) { const mo = MONTHS.findIndex(x => m[2].toLowerCase().startsWith(x.toLowerCase())); const y = +m[3], d = +m[1]; return mo >= 0 && valid(y, mo, d) ? iso(y, mo, d) : '' }
  return ''
}

const CSS = `
.hcdp-in{display:inline-flex;align-items:center;gap:8px;box-sizing:border-box;border:1px solid #d1d5db;border-radius:8px;background:#fff;padding:0 10px;height:34px;cursor:pointer;transition:border-color .12s, box-shadow .12s}
.hcdp-in:hover{border-color:#c9a84c}
.hcdp-in.open{border-color:#76601f;box-shadow:0 0 0 3px rgba(201,168,76,.18)}
.hcdp-in input{border:none;outline:none;background:transparent;font:inherit;font-size:13px;color:#0f2240;width:100%;min-width:0;cursor:pointer}
.hcdp-in input::placeholder{color:#5b6878}
.hcdp-pop{position:fixed;z-index:5000;width:272px;background:#fff;border:1px solid #e3e7ee;border-radius:14px;box-shadow:0 18px 50px rgba(10,22,40,.28);font-family:DM Sans,system-ui,-apple-system,sans-serif;color:#0f2240;overflow:hidden}
.hcdp-h{display:flex;align-items:center;justify-content:space-between;background:#0f2240;color:#fff;padding:10px 10px}
.hcdp-sel{appearance:none;-webkit-appearance:none;background:rgba(255,255,255,.1) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23c9a84c' stroke-width='1.6'/%3E%3C/svg%3E") no-repeat right 8px center;border:1px solid rgba(255,255,255,.14);border-radius:7px;color:#fff;font:700 13px/1 inherit;font-family:inherit;padding:6px 24px 6px 9px;cursor:pointer;outline:none}
.hcdp-sel:hover,.hcdp-sel:focus{border-color:#c9a84c}
.hcdp-sel option{color:#0f2240;background:#fff;font-weight:500}
.hcdp-nav{width:28px;height:28px;border:none;border-radius:7px;background:transparent;color:#fff;font-size:17px;cursor:pointer;line-height:1}
.hcdp-nav:hover{background:rgba(255,255,255,.12)}
.hcdp-g{display:grid;grid-template-columns:repeat(7,1fr);gap:2px;padding:8px 10px 4px}
.hcdp-dow{font-size:10.5px;font-weight:700;color:#5b6878;text-align:center;padding:4px 0;text-transform:uppercase;letter-spacing:.04em}
.hcdp-d{height:32px;border:none;border-radius:8px;background:transparent;font:500 13px/1 inherit;font-family:inherit;color:#0f2240;cursor:pointer}
.hcdp-d:hover{background:#f4efe0}
.hcdp-d.out{color:#c3cad4}
.hcdp-d.today{box-shadow:inset 0 0 0 1.5px #c9a84c;font-weight:700}
.hcdp-d.sel{background:#0f2240;color:#fff;font-weight:700}
.hcdp-f{display:flex;justify-content:space-between;padding:6px 12px 10px;border-top:1px solid #eef1f5}
.hcdp-f button{border:none;background:transparent;font:600 12.5px/1 inherit;font-family:inherit;cursor:pointer;padding:6px 8px;border-radius:6px}
.hcdp-f button:hover{background:#f5f7fa}
`
function ensureCss() {
  if (typeof document === 'undefined' || document.getElementById('hcdp-css')) return
  const s = document.createElement('style'); s.id = 'hcdp-css'; s.textContent = CSS; document.head.appendChild(s)
}

export default function DatePicker({ value, onChange, placeholder = 'Select date', style, width = 150, min, max, fromYear = 1930, toYear = new Date().getFullYear() + 30 }) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState(fmtDocDate(value))
  const base = parseDate(value) || todayIso()
  const [cursor, setCursor] = useState(() => { const [y, m] = base.split('-').map(Number); return { y, m: m - 1 } })
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const boxRef = useRef(null)
  const popRef = useRef(null)

  useEffect(() => { ensureCss() }, [])
  useEffect(() => { setText(fmtDocDate(value)) }, [value])

  function place() {
    const r = boxRef.current?.getBoundingClientRect()
    if (!r) return
    const W = 272, H = 340
    let left = Math.min(r.left, window.innerWidth - W - 8)
    let top = r.bottom + 6
    if (top + H > window.innerHeight && r.top - H - 6 > 0) top = r.top - H - 6
    setPos({ top, left: Math.max(8, left) })
  }
  function openPop() {
    const b = parseDate(value) || todayIso()
    const [y, m] = b.split('-').map(Number)
    setCursor({ y, m: m - 1 }); place(); setOpen(true)
  }
  useEffect(() => {
    if (!open) return
    const onDown = e => { if (!boxRef.current?.contains(e.target) && !popRef.current?.contains(e.target)) setOpen(false) }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    const onMove = () => place()
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); window.removeEventListener('scroll', onMove, true); window.removeEventListener('resize', onMove) }
  }, [open]) // eslint-disable-line

  const allowed = d => (!min || d >= min) && (!max || d <= max)
  function pick(d) { if (!allowed(d)) return; onChange && onChange(d); setOpen(false) }
  function commitText() {
    if (!text.trim()) { if (value) onChange && onChange(''); return }
    const p = parseDate(text)
    if (p && allowed(p)) { if (p !== value) onChange && onChange(p); setText(fmtDocDate(p)) }
    else setText(fmtDocDate(value))
  }
  const shift = n => setCursor(c => { const d = new Date(c.y, c.m + n, 1); return { y: d.getFullYear(), m: d.getMonth() } })

  const { y, m } = cursor
  const lead = (new Date(y, m, 1).getDay() + 6) % 7 // Monday first
  const cells = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(y, m, 1 - lead + i)
    cells.push({ iso: iso(d.getFullYear(), d.getMonth(), d.getDate()), day: d.getDate(), out: d.getMonth() !== m })
  }
  const sel = parseDate(value)
  const tIso = todayIso()
  const years = []
  for (let yr = Math.max(toYear, y); yr >= Math.min(fromYear, y); yr--) years.push(yr) // newest first

  return (
    <>
      <div ref={boxRef} className={'hcdp-in' + (open ? ' open' : '')} style={{ width, ...style }} onClick={() => !open && openPop()}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}><rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M3.5 10h17M8 3v4M16 3v4" /></svg>
        <input value={text} placeholder={placeholder} onChange={e => setText(e.target.value)}
          onBlur={commitText} onKeyDown={e => { if (e.key === 'Enter') { commitText(); setOpen(false); e.currentTarget.blur() } }} />
      </div>
      {open && createPortal(
        <div ref={popRef} className="hcdp-pop" style={{ top: pos.top, left: pos.left }}>
          <div className="hcdp-h">
            <button className="hcdp-nav" onClick={() => shift(-1)} aria-label="Previous month">&lsaquo;</button>
            <div style={{ display: 'flex', gap: 6 }}>
              <select className="hcdp-sel" value={m} onChange={e => setCursor(c => ({ ...c, m: +e.target.value }))}>
                {MONTHS_LONG.map((mn, i) => <option key={mn} value={i}>{mn}</option>)}
              </select>
              <select className="hcdp-sel" value={y} onChange={e => setCursor(c => ({ ...c, y: +e.target.value }))}>
                {years.map(yr => <option key={yr} value={yr}>{yr}</option>)}
              </select>
            </div>
            <button className="hcdp-nav" onClick={() => shift(1)} aria-label="Next month">&rsaquo;</button>
          </div>
          <div className="hcdp-g">
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => <div key={d} className="hcdp-dow">{d}</div>)}
            {cells.map(c => (
              <button key={c.iso} disabled={!allowed(c.iso)} onClick={() => pick(c.iso)}
                className={'hcdp-d' + (c.out ? ' out' : '') + (c.iso === tIso ? ' today' : '') + (c.iso === sel ? ' sel' : '')}
                style={!allowed(c.iso) ? { opacity: .3, cursor: 'default' } : undefined}>{c.day}</button>
            ))}
          </div>
          <div className="hcdp-f">
            <button style={{ color: '#5b6878' }} onClick={() => { onChange && onChange(''); setOpen(false) }}>Clear</button>
            <button style={{ color: '#76601f' }} onClick={() => pick(tIso)}>Today</button>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
